/**
 * Composant principal de l'application
 */

import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Provider } from 'react-redux';
import { useSelector, useDispatch } from 'react-redux';
import { store, AppDispatch } from './store';
import { ErrorBoundary } from './components/ui/ErrorBoundary';
import { getCurrentUser } from './store/slices/authSlice';
import './App.css';
import './animations.css';

// Layout
import { MainLayout } from './components/layout/MainLayout';
import { ALL_NAV_ITEMS } from './components/layout/Sidebar';
import { useGetParametreByKeyQuery } from './services/coreApi';

// Pages
import { Login } from './pages/Login';
import { Dashboard } from './pages/Dashboard';
import { EtudiantsList } from './pages/Etudiants/EtudiantsList';
import { EtudiantDetail } from './pages/Etudiants/EtudiantDetail';
import EtudiantForm from './pages/Etudiants/EtudiantForm';
import { ImportExcel } from './pages/Etudiants/ImportExcel';
import { MiseAJourExcel } from './pages/Etudiants/MiseAJourExcel';
import { NotesList } from './pages/Notes/NotesList';
import SaisieNotesDevoirs from './pages/SaisieNotesDevoirs';
import SaisieNotesExamens from './pages/SaisieNotesExamens';
import SuiviAntiReclamations from './pages/SuiviAntiReclamations';
import SaisieParCodeCopie from './pages/SaisieParCodeCopie';
import { ResultatsList } from './pages/Resultats/ResultatsList';
import TableauDeliberation from './pages/Deliberation/TableauDeliberation';
import { RelevesList } from './pages/Releves/RelevesList';
import { GenerationReleves } from './pages/Releves/GenerationReleves';
import RelevesNotes from './pages/Releves/RelevesNotes';
import { GestionMatieres } from './pages/Matieres/GestionMatieres';
import { SyntheseRepartitionUE } from './pages/Matieres/SyntheseRepartitionUE';
import ServiceScolarite from './pages/ServiceScolarite';
import RappelRapportJournalierModal from './components/ServiceScolarite/RappelRapportJournalierModal';
import { RachatIntelligent } from './pages/Rachat/RachatIntelligent';
import ChoixRattrapage from './pages/Rattrapage/ChoixRattrapage';
import VueMutualisee from './pages/RepartitionUE/VueMutualisee';
import { Statistiques } from './pages/Statistiques/Statistiques';
import StatistiquesParticipation from './pages/Statistiques/StatistiquesParticipation';
import BanqueSujetsAdmin from './pages/BanqueSujets';
import DepotSujetsAdmin from './pages/DepotSujets';
import AuditSujets from './pages/AuditSujets';
import AuditAgents from './pages/AuditAgents';
import EvolutionAnnuelle from './pages/EvolutionAnnuelle';
import { AssociationCopies } from './pages/Association/AssociationCopies';
import GenerationCopies from './pages/GenerationCopies';
import AssociationCopiesNew from './pages/AssociationCopies';
import RecapMatieresAssociees from './pages/AssociationCopies/RecapMatieresAssociees';
import GestionFraudes from './pages/Fraudes/GestionFraudes';
import AnnulationsSession from './pages/Fraudes/AnnulationsSession';
import BasculeSpecialisation from './pages/BasculeSpecialisation';
import Suggestions from './pages/Suggestions';
import SuiviEnveloppes from './pages/Enveloppes/SuiviEnveloppes';
import Settings from './pages/Settings';
import SelectionAnnee from './pages/SelectionAnnee';
import GestionOptions from './pages/GestionOptions';
import GestionFormateurs from './pages/Formateurs/GestionFormateurs';
import PVDeliberation from './pages/PVDeliberation';
import SuiviScolarite from './pages/SuiviScolarite';
import ComptesFormateurs from './pages/ComptesFormateurs';
import ConsultationNotes from './pages/Etudiants/ConsultationNotes';
import BudgetCorrection from './pages/BudgetCorrection';
import PortailAdminPage from './pages/PortailAdmin';
import ConsultationResultatsCaisse from './pages/Caisse/ConsultationResultats';
import { GestionAcces } from './pages/PortailAdmin/GestionAcces';
import { EtudiantsConnectes } from './pages/PortailAdmin/EtudiantsConnectes';
import MessagesEtudiantsPage from './pages/MessagesEtudiants';
import DemandesActivationPage from './pages/DemandesActivation';
import MessagesParentsPage from './pages/MessagesParents';
import ModerationCommentairesPage from './pages/ModerationCommentaires';
import ActualitesPage from './pages/Actualites';
import SurveillancePage from './pages/Surveillance';
import InscriptionsParentsPage from './pages/InscriptionsParents';
import EdtSurveillancePage from './pages/EdtSurveillance';
import JustificationsPage from './pages/Justifications';
import MonPlanningPage from './pages/MonPlanning';
import ConduitePage from './pages/Conduite';
import DashboardAbsencesPage from './pages/DashboardAbsences';
import SaisieNotesFormateurPage from './pages/SaisieNotesFormateur';
import FenetresSaisiePage from './pages/FenetresSaisie';
import PortailRoutes from './portail/PortailRoutes';
import FormateurRoutes from './formateur/FormateurRoutes';
import SurveillantRoutes from './surveillant/SurveillantRoutes';
import VerificationRoutes from './verification/VerificationRoutes';
import AppsStore from './pages/AppsStore';
import MessagerieInterneAdmin from './pages/MessagerieInterne';
import Parents from './pages/Parents';
import AuditCredits from './pages/AuditCredits';
import MessagerieChat from './pages/Messagerie';
import BilanEtudiant from './pages/Etudiants/BilanEtudiant';
import { useHeartbeatPresence } from './hooks/useHeartbeatPresence';
import RepartitionUE from './pages/RepartitionUE';
import MigrationAnnee from './pages/MigrationAnnee';
import Salles from './pages/Salles';
import ImpressionEDT from './pages/ImpressionEDT';
import OccupationSalles from './pages/OccupationSalles';
import PresenceFormateurs from './pages/PresenceFormateurs';
import RepartitionIA from './pages/RepartitionUE/RepartitionIA';
import AuditNotesOCR from './pages/AuditNotesOCR';
import GestionTP from './pages/GestionTP';
import Brassage from './pages/Brassage';
import EmploisTempsPage from './pages/EmploisTemps';
import ListeInscrits from './pages/ListeInscrits';
import UploadAutomatiqueQR from './pages/UploadAutomatiqueQR';
import Recouvrement from './pages/Recouvrement';

// Nouvelles pages OCR avec relevés
import {
  GenerationReleveDevoirs,
  GenerationReleveCorrection,
  VerificationOCRDevoirs,
  VerificationOCRCorrection,
  GenerationReleveCorrectionV2,
  VerificationOCRCorrectionV2,
  SaisieRapideRelevesV2,
  ReclamationsNotes,
  SaisieRapideRelevesDevoirs,
  SaisieRapideExamens,
} from './pages/OCR';

// Récupère l'utilisateur courant au chargement si un token existe
const AuthInitializer: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const token = useSelector((state: any) => state.auth.token);
  const user = useSelector((state: any) => state.auth.user);

  useEffect(() => {
    if (token && !user) {
      dispatch(getCurrentUser());
    }
  }, [dispatch, token, user]);

  return null;
};

/**
 * Si l'app tourne en mode PWA installée (display-mode standalone) et que l'URL
 * d'ouverture pointe hors /portail/ (cas des anciennes installations qui
 * pointaient vers /login admin), on redirige vers le portail étudiant.
 */
const PWAStandaloneGuard: React.FC = () => {
  useEffect(() => {
    try {
      const isStandalone =
        window.matchMedia('(display-mode: standalone)').matches ||
        // iOS Safari
        (window.navigator as any).standalone === true;
      const path = window.location.pathname;
      // Autorise /portail/* (étudiant), /formateur/* (formateur), /surveillant/* (surveillant)
      // et /verif/* (PWA vérification publique BZ). Sinon retombe sur portail étudiant.
      if (isStandalone
          && !path.startsWith('/portail/')
          && !path.startsWith('/formateur/')
          && !path.startsWith('/surveillant/')
          && !path.startsWith('/verif/')) {
        // CRITIQUE : nettoyer les tokens backoffice stales pour éviter la boucle
        // /login <-> /portail/login sur les PWA installées où un ancien admin
        // avait laissé un auth_token dans localStorage (Login.tsx auto-nav vers
        // /selection-annee si isAuthenticated=true, ce qui déclenche à nouveau
        // le guard qui redirige vers /portail/login, ad infinitum).
        try {
          localStorage.removeItem('auth_token');
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('user_data');
        } catch { /* ignore */ }
        window.location.replace('/portail/login');
      }
    } catch { /* ignore */ }
  }, []);
  return null;
};

// Theme & Density bridge: applies dark class and data-density attribute on <html>
const ThemeManager: React.FC = () => {
  const theme = useSelector((state: any) => state.ui.theme);
  const density = useSelector((state: any) => state.ui.density);

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [theme]);

  useEffect(() => {
    document.documentElement.setAttribute('data-density', density);
  }, [density]);

  return null;
};

// Whitelist des rubriques accessibles aux rôles `agent`/`agent_caisse`
// quand le paramètre admin `mode_restreint_agent_examen` est activé.
// Doit rester alignée avec `AGENT_RESTREINT_EXAMEN_KEYS` de la Sidebar.
/** Fix 2026-08-31 : page « étudiants en ligne » standalone, réutilise le
 *  composant admin `EtudiantsConnectes` mais accessible aussi aux formateurs,
 *  agents et surveillants sans leur ouvrir tout le portail admin. */
const EtudiantsEnLignePage: React.FC = () => (
  <div className="max-w-screen-2xl mx-auto space-y-4">
    <div>
      <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Étudiants en ligne</h1>
      <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
        Vue temps réel des étudiants actuellement connectés au portail
      </p>
    </div>
    <EtudiantsConnectes />
  </div>
);

const RESTRICTED_AGENT_ALLOWED_PATHS = new Set([
  '/', '/dashboard',
  '/etudiants', '/etudiants/',
  '/service-scolarite',
  '/formateurs',
  '/association-copies', '/association-copies-barcode',
  '/emplois-temps',
  '/statistiques-participation',
  '/messagerie',
  '/caisse/resultats', '/caisse/acces',
]);

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isAuthenticated = useSelector((state: any) => state.auth.isAuthenticated);
  const role = useSelector((state: any) => state.auth.user?.role);
  const userId = useSelector((state: any) => state.auth.user?.id);
  const location = useLocation();
  const { data: modeRestreintParam } = useGetParametreByKeyQuery('mode_restreint_agent_examen');
  const { data: modeRestreintExceptionsParam } = useGetParametreByKeyQuery('mode_restreint_agent_examen_exceptions');
  // Fix 2026-08-28 : honorer la surcouche onglets_visibilite_user (shown)
  // pour ne PAS bloquer l'accès à un onglet ajouté explicitement à un user
  // en mode restreint (Paramètres → Visibilité onglets → Par utilisateur).
  const { data: visibiliteUserParam } = useGetParametreByKeyQuery('onglets_visibilite_user');
  // Heartbeat présence (envoie page courante toutes les 30s)
  useHeartbeatPresence(isAuthenticated);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  // Les formateurs n'ont pas accès au back-office : tout dans /formateur.
  if (role === 'formateur') {
    return <Navigate to="/formateur" replace />;
  }

  // Mode restreint session d'examens : redirige les agents hors whitelist
  // vers le dashboard (l'onglet visé n'apparaît plus dans la sidebar, mais
  // l'URL directe reste théoriquement tapable — on garde-fou ici aussi).
  const raw = (modeRestreintParam as any)?.valeur_convertie ?? (modeRestreintParam as any)?.valeur;
  const modeRestreintActif = raw === true || raw === 'true' || raw === '1' || raw === 1;
  const exceptionsRaw: any =
    (modeRestreintExceptionsParam as any)?.valeur_convertie ?? (modeRestreintExceptionsParam as any)?.valeur;
  let estExcepte = false;
  try {
    const arr = typeof exceptionsRaw === 'string' ? JSON.parse(exceptionsRaw) : exceptionsRaw;
    if (Array.isArray(arr) && userId != null) {
      estExcepte = arr.map((x: any) => Number(x)).includes(Number(userId));
    }
  } catch { /* ignore */ }
  if (modeRestreintActif && !estExcepte && (role === 'agent' || role === 'agent_caisse')) {
    // Normalise le path (retire query/hash)
    const path = location.pathname.replace(/\/$/, '') || '/';
    // Whitelist statique du mode restreint
    let allowed = RESTRICTED_AGENT_ALLOWED_PATHS.has(location.pathname)
      || RESTRICTED_AGENT_ALLOWED_PATHS.has(path);

    // Fix 2026-08-28 : ajoute les onglets EXPLICITEMENT donnés à cet
    // utilisateur via Paramètres → Visibilité onglets → Par utilisateur.
    // Sans ça, la sidebar affiche l'onglet mais le route redirige vers
    // /dashboard → l'utilisateur ne peut pas y accéder.
    if (!allowed && userId != null) {
      const rawU: any =
        (visibiliteUserParam as any)?.valeur_convertie ?? (visibiliteUserParam as any)?.valeur;
      let shownKeys: string[] = [];
      try {
        const configU = typeof rawU === 'string' ? JSON.parse(rawU) : rawU;
        if (configU && typeof configU === 'object' && configU.shown) {
          const uid = String(userId);
          shownKeys = configU.shown[uid] || [];
        }
      } catch { /* ignore */ }
      if (shownKeys.length > 0) {
        // Traduit les clés en paths via ALL_NAV_ITEMS puis matche exactement.
        const shownPaths = new Set(
          ALL_NAV_ITEMS
            .filter(item => shownKeys.includes(item.key))
            .map(item => item.path)
        );
        // Match soit exact soit préfixe (sous-routes, ex: /etudiants/123)
        allowed = shownPaths.has(path) || shownPaths.has(location.pathname)
          || Array.from(shownPaths).some(p =>
              p && p !== '/' && (location.pathname === p || location.pathname.startsWith(p + '/'))
            );
      }
    }

    if (!allowed) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return <MainLayout>{children}</MainLayout>;
};

// Protected Route sans layout (pour la sélection d'année)
const ProtectedRouteNoLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isAuthenticated = useSelector((state: any) => state.auth.isAuthenticated);
  const role = useSelector((state: any) => state.auth.user?.role);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  if (role === 'formateur') {
    return <Navigate to="/formateur" replace />;
  }

  return <>{children}</>;
};

function App() {
  return (
    <Provider store={store}>
      <PWAStandaloneGuard />
      <AuthInitializer />
      <ThemeManager />
      <Router>
        <div className="App min-h-screen bg-gray-50 dark:bg-gray-900 dark:text-gray-100 transition-colors">
          <ErrorBoundary context="Une erreur d'affichage s'est produite. Vos données ne sont pas perdues — rechargez la page.">
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<Login />} />

            {/* PWA Portail étudiant (public, hors layout admin) */}
            <Route path="/portail/*" element={<PortailRoutes />} />
            <Route path="/formateur/*" element={<FormateurRoutes />} />
            <Route path="/surveillant/*" element={<SurveillantRoutes />} />
            <Route path="/verif/*" element={<VerificationRoutes />} />

            {/* Mini-store public d'applications mobiles (sans auth) */}
            <Route path="/apps" element={<AppsStore />} />

            {/* Sélection d'année (après login, sans layout) */}
            <Route
              path="/selection-annee"
              element={
                <ProtectedRouteNoLayout>
                  <SelectionAnnee />
                </ProtectedRouteNoLayout>
              }
            />

            {/* Protected Routes */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />

            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/messages-etudiants"
              element={
                <ProtectedRoute>
                  <MessagesEtudiantsPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/demandes-activation"
              element={
                <ProtectedRoute>
                  <DemandesActivationPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/messages-parents"
              element={
                <ProtectedRoute>
                  <MessagesParentsPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/moderation-commentaires"
              element={
                <ProtectedRoute>
                  <ModerationCommentairesPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/actualites"
              element={
                <ProtectedRoute>
                  <ActualitesPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/surveillance"
              element={
                <ProtectedRoute>
                  <SurveillancePage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/inscriptions-parents"
              element={
                <ProtectedRoute>
                  <InscriptionsParentsPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/edt-surveillance"
              element={
                <ProtectedRoute>
                  <EdtSurveillancePage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/justifications"
              element={
                <ProtectedRoute>
                  <JustificationsPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/mon-planning"
              element={
                <ProtectedRoute>
                  <MonPlanningPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/conduite-eleves"
              element={
                <ProtectedRoute>
                  <ConduitePage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard-absences"
              element={
                <ProtectedRoute>
                  <DashboardAbsencesPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/saisie-notes-formateur"
              element={
                <ProtectedRoute>
                  <SaisieNotesFormateurPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/fenetres-saisie"
              element={
                <ProtectedRoute>
                  <FenetresSaisiePage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/etudiants"
              element={
                <ProtectedRoute>
                  <EtudiantsList />
                </ProtectedRoute>
              }
            />

            <Route
              path="/etudiants/:matricule/bilan"
              element={
                <ProtectedRoute>
                  <BilanEtudiant />
                </ProtectedRoute>
              }
            />

            <Route
              path="/etudiants/:id"
              element={
                <ProtectedRoute>
                  <EtudiantDetail />
                </ProtectedRoute>
              }
            />

            <Route
              path="/etudiants/:id/notes"
              element={
                <ProtectedRoute>
                  <ConsultationNotes />
                </ProtectedRoute>
              }
            />

            {/* Create / Edit Etudiant */}
            <Route
              path="/etudiants/new"
              element={
                <ProtectedRoute>
                  <EtudiantForm />
                </ProtectedRoute>
              }
            />

            <Route
              path="/etudiants/:id/edit"
              element={
                <ProtectedRoute>
                  <EtudiantForm />
                </ProtectedRoute>
              }
            />

            <Route
              path="/etudiants/import"
              element={
                <ProtectedRoute>
                  <ImportExcel />
                </ProtectedRoute>
              }
            />

            <Route
              path="/etudiants/mise-a-jour"
              element={
                <ProtectedRoute>
                  <MiseAJourExcel />
                </ProtectedRoute>
              }
            />

            <Route
              path="/notes"
              element={
                <ProtectedRoute>
                  <NotesList />
                </ProtectedRoute>
              }
            />

            <Route
              path="/saisie-notes-devoirs"
              element={
                <ProtectedRoute>
                  <SaisieNotesDevoirs />
                </ProtectedRoute>
              }
            />

            <Route
              path="/saisie-notes-examens"
              element={
                <ProtectedRoute>
                  <SaisieNotesExamens />
                </ProtectedRoute>
              }
            />

            {/* Fix 2026-09-08 : dispositif anti-réclamations notes */}
            <Route
              path="/suivi-anti-reclamations"
              element={
                <ProtectedRoute>
                  <SuiviAntiReclamations />
                </ProtectedRoute>
              }
            />

            {/* Fix 2026-09-02 : saisie par code de copie (relevés OCR pâles) */}
            <Route
              path="/saisie-par-code-copie"
              element={
                <ProtectedRoute>
                  <SaisieParCodeCopie />
                </ProtectedRoute>
              }
            />

            <Route
              path="/resultats"
              element={
                <ProtectedRoute>
                  <ResultatsList />
                </ProtectedRoute>
              }
            />

            <Route
              path="/deliberation"
              element={
                <ProtectedRoute>
                  <TableauDeliberation />
                </ProtectedRoute>
              }
            />

            <Route
              path="/suivi-scolarite"
              element={
                <ProtectedRoute>
                  <SuiviScolarite />
                </ProtectedRoute>
              }
            />

            <Route
              path="/comptes-formateurs"
              element={
                <ProtectedRoute>
                  <ComptesFormateurs />
                </ProtectedRoute>
              }
            />

            <Route
              path="/releves"
              element={
                <ProtectedRoute>
                  <RelevesList />
                </ProtectedRoute>
              }
            />

            <Route
              path="/releves/generation"
              element={
                <ProtectedRoute>
                  <GenerationReleves />
                </ProtectedRoute>
              }
            />

            <Route
              path="/releves/notes"
              element={
                <ProtectedRoute>
                  <RelevesNotes />
                </ProtectedRoute>
              }
            />

            <Route
              path="/matieres"
              element={
                <ProtectedRoute>
                  <GestionMatieres />
                </ProtectedRoute>
              }
            />

            <Route
              path="/synthese-repartition-ue"
              element={
                <ProtectedRoute>
                  <SyntheseRepartitionUE />
                </ProtectedRoute>
              }
            />

            <Route
              path="/service-scolarite"
              element={
                <ProtectedRoute>
                  <ServiceScolarite />
                </ProtectedRoute>
              }
            />

            <Route
              path="/rachat-intelligent"
              element={
                <ProtectedRoute>
                  <RachatIntelligent />
                </ProtectedRoute>
              }
            />

            <Route
              path="/rattrapage/choix"
              element={
                <ProtectedRoute>
                  <ChoixRattrapage />
                </ProtectedRoute>
              }
            />

            {/* Redirect ancien URL vers Settings */}
            <Route path="/annee-academique" element={<Navigate to="/settings" replace />} />

            <Route
              path="/statistiques"
              element={
                <ProtectedRoute>
                  <Statistiques />
                </ProtectedRoute>
              }
            />
            <Route
              path="/statistiques-participation"
              element={
                <ProtectedRoute>
                  <StatistiquesParticipation />
                </ProtectedRoute>
              }
            />
            <Route
              path="/evolution-annuelle"
              element={
                <ProtectedRoute>
                  <EvolutionAnnuelle />
                </ProtectedRoute>
              }
            />
            <Route
              path="/banque-sujets"
              element={
                <ProtectedRoute>
                  <BanqueSujetsAdmin />
                </ProtectedRoute>
              }
            />
            <Route
              path="/depot-sujets"
              element={
                <ProtectedRoute>
                  <DepotSujetsAdmin />
                </ProtectedRoute>
              }
            />
            <Route
              path="/audit-sujets"
              element={
                <ProtectedRoute>
                  <AuditSujets />
                </ProtectedRoute>
              }
            />
            <Route
              path="/audit-agents"
              element={
                <ProtectedRoute>
                  <AuditAgents />
                </ProtectedRoute>
              }
            />

            <Route
              path="/association-copies"
              element={
                <ProtectedRoute>
                  <AssociationCopies />
                </ProtectedRoute>
              }
            />

            <Route
              path="/generation-copies"
              element={
                <ProtectedRoute>
                  <GenerationCopies />
                </ProtectedRoute>
              }
            />

            <Route
              path="/association-copies-barcode"
              element={
                <ProtectedRoute>
                  <AssociationCopiesNew />
                </ProtectedRoute>
              }
            />

            <Route
              path="/recap-matieres-associees"
              element={
                <ProtectedRoute>
                  <RecapMatieresAssociees />
                </ProtectedRoute>
              }
            />

            <Route
              path="/fraudes"
              element={
                <ProtectedRoute>
                  <GestionFraudes />
                </ProtectedRoute>
              }
            />

            <Route
              path="/annulations-session"
              element={
                <ProtectedRoute>
                  <AnnulationsSession />
                </ProtectedRoute>
              }
            />

            <Route
              path="/bascule-specialisation"
              element={
                <ProtectedRoute>
                  <BasculeSpecialisation />
                </ProtectedRoute>
              }
            />

            <Route
              path="/suggestions"
              element={
                <ProtectedRoute>
                  <Suggestions />
                </ProtectedRoute>
              }
            />

            <Route
              path="/enveloppes"
              element={
                <ProtectedRoute>
                  <SuiviEnveloppes />
                </ProtectedRoute>
              }
            />

            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              }
            />

            <Route
              path="/gestion-options"
              element={
                <ProtectedRoute>
                  <GestionOptions />
                </ProtectedRoute>
              }
            />

            <Route
              path="/formateurs"
              element={
                <ProtectedRoute>
                  <GestionFormateurs />
                </ProtectedRoute>
              }
            />

            <Route
              path="/pv-deliberation"
              element={
                <ProtectedRoute>
                  <PVDeliberation />
                </ProtectedRoute>
              }
            />

            <Route
              path="/brassage"
              element={
                <ProtectedRoute>
                  <Brassage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/emplois-temps"
              element={
                <ProtectedRoute>
                  <EmploisTempsPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/budget-correction"
              element={
                <ProtectedRoute>
                  <BudgetCorrection />
                </ProtectedRoute>
              }
            />

            <Route
              path="/repartition-ue"
              element={
                <ProtectedRoute>
                  <RepartitionUE />
                </ProtectedRoute>
              }
            />

            <Route
              path="/migration-annee"
              element={
                <ProtectedRoute>
                  <MigrationAnnee />
                </ProtectedRoute>
              }
            />

            <Route
              path="/salles"
              element={
                <ProtectedRoute>
                  <Salles />
                </ProtectedRoute>
              }
            />

            <Route
              path="/impression-edt"
              element={
                <ProtectedRoute>
                  <ImpressionEDT />
                </ProtectedRoute>
              }
            />

            <Route
              path="/occupation-salles"
              element={
                <ProtectedRoute>
                  <OccupationSalles />
                </ProtectedRoute>
              }
            />

            <Route
              path="/presence-formateurs"
              element={
                <ProtectedRoute>
                  <PresenceFormateurs />
                </ProtectedRoute>
              }
            />

            <Route
              path="/repartition-ue/mutualisee"
              element={
                <ProtectedRoute>
                  <VueMutualisee />
                </ProtectedRoute>
              }
            />

            <Route
              path="/repartition-ue/ia"
              element={
                <ProtectedRoute>
                  <RepartitionIA />
                </ProtectedRoute>
              }
            />

            <Route
              path="/audit-notes-ocr"
              element={
                <ProtectedRoute>
                  <AuditNotesOCR />
                </ProtectedRoute>
              }
            />

            <Route
              path="/gestion-tp"
              element={
                <ProtectedRoute>
                  <GestionTP />
                </ProtectedRoute>
              }
            />

            <Route
              path="/liste-inscrits"
              element={
                <ProtectedRoute>
                  <ListeInscrits />
                </ProtectedRoute>
              }
            />

            {/* ========== Nouvelles routes OCR avec relevés ========== */}
            <Route
              path="/ocr/devoirs"
              element={
                <ProtectedRoute>
                  <GenerationReleveDevoirs />
                </ProtectedRoute>
              }
            />

            <Route
              path="/ocr/correction"
              element={
                <ProtectedRoute>
                  <GenerationReleveCorrection />
                </ProtectedRoute>
              }
            />

            <Route
              path="/ocr/verification-devoirs/:releveId"
              element={
                <ProtectedRoute>
                  <VerificationOCRDevoirs />
                </ProtectedRoute>
              }
            />

            <Route
              path="/ocr/verification-correction/:releveId"
              element={
                <ProtectedRoute>
                  <VerificationOCRCorrection />
                </ProtectedRoute>
              }
            />

            {/* ========== Relevés Examens format manuscrit (V2) ========== */}
            <Route
              path="/ocr/generation-correction-v2"
              element={
                <ProtectedRoute>
                  <GenerationReleveCorrectionV2 />
                </ProtectedRoute>
              }
            />

            {/* ========== Routes OCR V2 (vérification et saisie rapide) ========== */}
            <Route
              path="/ocr/verification-correction-v2/:releveId"
              element={
                <ProtectedRoute>
                  <VerificationOCRCorrectionV2 />
                </ProtectedRoute>
              }
            />

            <Route
              path="/ocr/saisie-rapide-v2/:releveId"
              element={
                <ProtectedRoute>
                  <SaisieRapideRelevesV2 />
                </ProtectedRoute>
              }
            />

            <Route
              path="/ocr/reclamations"
              element={
                <ProtectedRoute>
                  <ReclamationsNotes />
                </ProtectedRoute>
              }
            />

            <Route
              path="/ocr/saisie-rapide-devoirs"
              element={
                <ProtectedRoute>
                  <SaisieRapideRelevesDevoirs />
                </ProtectedRoute>
              }
            />

            <Route
              path="/ocr/saisie-rapide-examens"
              element={
                <ProtectedRoute>
                  <SaisieRapideExamens />
                </ProtectedRoute>
              }
            />

            <Route
              path="/ocr/upload-automatique-qr"
              element={
                <ProtectedRoute>
                  <UploadAutomatiqueQR />
                </ProtectedRoute>
              }
            />

            {/* Caisse (2026-10-02) : résultats par nom (deux semestres côte à côte)
                et gestion des accès étudiants, sans le reste du portail admin. */}
            <Route
              path="/caisse/resultats"
              element={
                <ProtectedRoute>
                  <ConsultationResultatsCaisse />
                </ProtectedRoute>
              }
            />
            <Route
              path="/caisse/acces"
              element={
                <ProtectedRoute>
                  <div className="max-w-screen-2xl mx-auto space-y-4">
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Accès étudiants</h1>
                    <GestionAcces />
                  </div>
                </ProtectedRoute>
              }
            />

            <Route
              path="/portail-admin"
              element={
                <ProtectedRoute>
                  <PortailAdminPage />
                </ProtectedRoute>
              }
            />

            {/* Fix 2026-08-31 : page dédiée « étudiants en ligne » accessible
                aussi aux formateurs / surveillants / directeurs sans leur
                donner l'accès complet au portail admin. */}
            <Route
              path="/etudiants-en-ligne"
              element={
                <ProtectedRoute>
                  <EtudiantsEnLignePage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/recouvrement"
              element={
                <ProtectedRoute>
                  <Recouvrement />
                </ProtectedRoute>
              }
            />

            <Route
              path="/messagerie-interne"
              element={
                <ProtectedRoute>
                  <MessagerieInterneAdmin />
                </ProtectedRoute>
              }
            />

            <Route
              path="/messagerie"
              element={
                <ProtectedRoute>
                  <MessagerieChat />
                </ProtectedRoute>
              }
            />

            <Route
              path="/parents"
              element={
                <ProtectedRoute>
                  <Parents />
                </ProtectedRoute>
              }
            />

            <Route
              path="/audit-credits"
              element={
                <ProtectedRoute>
                  <AuditCredits />
                </ProtectedRoute>
              }
            />

            {/* 404 Route */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
          </ErrorBoundary>
          {/* Modal global : rappel des tâches en retard pour agents/responsables */}
          {/* Modal global : rappel du rapport journalier après 17 h */}
          <RappelRapportJournalierModal />
          {/* Garde global : verrouille l'app si rapport journalier manquant (72h) */}
        </div>
      </Router>
    </Provider>
  );
}

export default App;
